// src/components/Admin/FirmDetailView.js
import React, { useState, useEffect } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Paper,
    Table,
    TableContainer,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    CircularProgress,
    Alert,
    IconButton,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    Chip,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import PersonRemoveIcon from '@mui/icons-material/PersonRemove';
import { db, functions } from '../../firebase';
import { collection, query, where, getDocs, doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { toast } from 'react-toastify';
import { getAuth } from 'firebase/auth';

const FirmDetailView = ({ firm, onBack }) => {
    const [members, setMembers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
    const [userToRemove, setUserToRemove] = useState(null);
    const [isRemoving, setIsRemoving] = useState(false);
    const [currentTier, setCurrentTier] = useState(firm?.firmPortalTier || 'read-only');
    const [isSavingTier, setIsSavingTier] = useState(false);

    useEffect(() => {
        const fetchMembers = async () => {
            if (!firm?.id) return;
            
            try {
                setLoading(true);
                setError(null);
                
                const membersQuery = query(
                    collection(db, 'users'),
                    where('organizationId', '==', firm.id)
                );
                
                const querySnapshot = await getDocs(membersQuery);
const membersList = querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
}));

// Filter out the owner from the members list
const filteredMembers = membersList.filter(member => member.id !== firm.ownerId);

setMembers(filteredMembers);
            } catch (err) {
                console.error('Error fetching members:', err);
                setError('Failed to load firm members.');
            } finally {
                setLoading(false);
            }
        };

        fetchMembers();
    }, [firm]);

    const handleRemoveClick = (user) => {
        setUserToRemove(user);
        setRemoveDialogOpen(true);
    };

    const confirmRemoveUser = async () => {
            if (!userToRemove) return;
    
            setIsRemoving(true);
            try {
                const removeUserFromFirm = httpsCallable(functions, 'removeUserFromFirm');
                const result = await removeUserFromFirm({
                    userId: userToRemove.id,
                    organizationId: firm.id
                });

                if (result.data.success) {
                    toast.success(`${userToRemove.name || userToRemove.email} has been removed from the firm.`);
                    
                    // Remove from local state
                    setMembers(prev => prev.filter(m => m.id !== userToRemove.id));
                    
                    setRemoveDialogOpen(false);
                    setUserToRemove(null);
                } else {
                    throw new Error(result.data.error || 'An unknown error occurred.');
                }
        } catch (err) {
            console.error('Error removing user from firm:', err);
            toast.error(`Failed to remove user: ${err.message}`);
        } finally {
            setIsRemoving(false);
        }
    };

    const handleTierChange = async (newTier) => {
        setIsSavingTier(true);
        try {
            const orgRef = doc(db, 'organizations', firm.id);
            await updateDoc(orgRef, {
                firmPortalTier: newTier
            });
            setCurrentTier(newTier);
            toast.success(`Portal tier updated to ${newTier === 'enterprise' ? 'Enterprise' : 'Read-Only'}.`);
        } catch (err) {
            console.error('Error updating tier:', err);
            toast.error(`Failed to update tier: ${err.message}`);
        } finally {
            setIsSavingTier(false);
        }
    };

    if (!firm) {
        return <Typography>No firm selected.</Typography>;
    }

    return (
        <Box>
            <Paper sx={{ p: 3 }}>
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={onBack}
                    sx={{ mb: 2 }}
                >
                    Back to Firm List
                </Button>
                
                <Typography variant="h5" gutterBottom>
                    Managing Firm: {firm.name}
                </Typography>
                 <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Owner: {firm.ownerEmail}
                </Typography>

                {/* Firm Settings Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Firm Settings
                </Typography>
                <Paper elevation={0} variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Typography variant="body1" sx={{ minWidth: 100 }}>
                            Portal Tier:
                        </Typography>
                        <FormControl size="small" sx={{ minWidth: 300 }}>
                            <Select
                                value={currentTier}
                                onChange={(e) => handleTierChange(e.target.value)}
                                disabled={isSavingTier}
                            >
                                <MenuItem value="read-only">Read-Only (Activity Feed + Alerts)</MenuItem>
                                <MenuItem value="enterprise">Enterprise (Full Dashboard + Management)</MenuItem>
                            </Select>
                        </FormControl>
                        {isSavingTier && <CircularProgress size={20} />}
                        <Chip 
                            label={currentTier === 'enterprise' ? 'Enterprise' : 'Read-Only'}
                            color={currentTier === 'enterprise' ? 'primary' : 'default'}
                            size="small"
                        />
                    </Box>
                </Paper>

                {/* Member Roster Section */}
                <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                    Firm Members
                </Typography>

                {loading && <CircularProgress />}
                {error && <Alert severity="error">{error}</Alert>}

                {!loading && !error && (
                    <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ mt: 2 }}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>User Type</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }}>Active Clients</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold' }} align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {members.length > 0 ? (
                                    members.map((member) => (
                                        <TableRow key={member.id} hover>
                                            <TableCell>{member.name || 'N/A'}</TableCell>
                                            <TableCell>{member.email}</TableCell>
                                            <TableCell>{member.userType || 'N/A'}</TableCell>
                                            <TableCell>{member.activeClientCount || 0}</TableCell>
                                            <TableCell align="right">
                                                <Tooltip title="Remove from Firm">
                                                    <IconButton 
                                                        size="small" 
                                                        color="error"
                                                        onClick={() => handleRemoveClick(member)}
                                                    >
                                                        <PersonRemoveIcon />
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                ) : (
                                    <TableRow>
                                        <TableCell colSpan={5} align="center">
                                            No members found in this firm.
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Paper>

            {/* Remove Confirmation Dialog */}
            <Dialog open={removeDialogOpen} onClose={() => setRemoveDialogOpen(false)}>
                <DialogTitle>Remove User from Firm</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Are you sure you want to remove <strong>{userToRemove?.name || userToRemove?.email}</strong> from the firm "{firm.name}"?
                    </DialogContentText>
                    <DialogContentText sx={{ mt: 2 }}>
                        This will unassign them from the firm but will NOT delete their user account. They will become an independent FCFO.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRemoveDialogOpen(false)} disabled={isRemoving}>
                        Cancel
                    </Button>
                    <Button
                        onClick={confirmRemoveUser}
                        variant="contained"
                        color="error"
                        disabled={isRemoving}
                    >
                        {isRemoving ? <CircularProgress size={24} /> : 'Remove from Firm'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default FirmDetailView;